// NAV Service - Firestore operations for NAV history tracking
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { NAVHistory } from '../types';

const NAV_HISTORY_COLLECTION = 'navHistory';

/**
 * Record a new NAV entry in history
 */
export const recordNAVHistory = async (navData: Omit<NAVHistory, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, NAV_HISTORY_COLLECTION), {
      ...navData,
      timestamp: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error recording NAV history:', error);
    throw new Error('Failed to record NAV history');
  }
};

/**
 * Get NAV history for a specific series
 */
export const getSeriesNAVHistory = async (
  seriesId: string,
  days?: number
): Promise<NAVHistory[]> => {
  try {
    let q = query(
      collection(db, NAV_HISTORY_COLLECTION),
      where('seriesId', '==', seriesId),
      orderBy('timestamp', 'desc')
    );

    if (days) {
      q = query(q, limit(days));
    }

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NAVHistory[];
  } catch (error) {
    console.error('Error getting series NAV history:', error);
    throw new Error('Failed to fetch series NAV history');
  }
};

/**
 * Get NAV history for the aggregated pool
 */
export const getPoolNAVHistory = async (days?: number): Promise<NAVHistory[]> => {
  try {
    let q = query(
      collection(db, NAV_HISTORY_COLLECTION),
      where('type', '==', 'aggregated_pool'),
      orderBy('date', 'desc') // Get newest first
    );

    if (days) {
      q = query(q, limit(days));
    }

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        // Convert Firestore Timestamps to JavaScript Dates
        date: data.date?.toDate ? data.date.toDate() : data.date,
        timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp,
      } as unknown as NAVHistory;
    });
  } catch (error) {
    console.error('Error getting pool NAV history:', error);
    throw new Error('Failed to fetch pool NAV history');
  }
};

/**
 * Get NAV history for a date range
 */
export const getNAVHistoryByDateRange = async (
  seriesId: string,
  startDate: Date,
  endDate: Date
): Promise<NAVHistory[]> => {
  try {
    const q = query(
      collection(db, NAV_HISTORY_COLLECTION),
      where('seriesId', '==', seriesId),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NAVHistory[];
  } catch (error) {
    console.error('Error getting NAV history by date range:', error);
    throw new Error('Failed to fetch NAV history for date range');
  }
};

/**
 * Get latest NAV for a series
 */
export const getLatestSeriesNAV = async (seriesId: string): Promise<NAVHistory | null> => {
  try {
    const q = query(
      collection(db, NAV_HISTORY_COLLECTION),
      where('seriesId', '==', seriesId),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as NAVHistory;
  } catch (error) {
    console.error('Error getting latest series NAV:', error);
    throw new Error('Failed to fetch latest series NAV');
  }
};

/**
 * Get latest aggregated pool NAV
 */
export const getLatestPoolNAV = async (): Promise<NAVHistory | null> => {
  try {
    const q = query(
      collection(db, NAV_HISTORY_COLLECTION),
      where('type', '==', 'aggregated_pool'),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as NAVHistory;
  } catch (error) {
    console.error('Error getting latest pool NAV:', error);
    throw new Error('Failed to fetch latest pool NAV');
  }
};

/**
 * Subscribe to real-time NAV history updates for a series
 */
export const subscribeSeriesNAVHistory = (
  seriesId: string,
  callback: (history: NAVHistory[]) => void,
  days?: number,
  onError?: (error: Error) => void
) => {
  let q = query(
    collection(db, NAV_HISTORY_COLLECTION),
    where('seriesId', '==', seriesId),
    orderBy('timestamp', 'desc')
  );

  if (days) {
    q = query(q, limit(days));
  }

  return onSnapshot(
    q,
    (snapshot) => {
      const history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as NAVHistory[];
      callback(history);
    },
    (error) => {
      console.error('Error in NAV history subscription:', error);
      if (onError) {
        onError(new Error('Failed to subscribe to NAV history'));
      }
    }
  );
};

/**
 * Subscribe to real-time pool NAV history updates
 */
export const subscribePoolNAVHistory = (
  callback: (history: NAVHistory[]) => void,
  days?: number,
  onError?: (error: Error) => void
) => {
  let q = query(
    collection(db, NAV_HISTORY_COLLECTION),
    where('type', '==', 'aggregated_pool'),
    orderBy('timestamp', 'desc')
  );

  if (days) {
    q = query(q, limit(days));
  }

  return onSnapshot(
    q,
    (snapshot) => {
      const history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as NAVHistory[];
      callback(history);
    },
    (error) => {
      console.error('Error in pool NAV history subscription:', error);
      if (onError) {
        onError(new Error('Failed to subscribe to pool NAV history'));
      }
    }
  );
};

/**
 * Calculate NAV change percentage
 */
export const calculateNAVChange = async (
  seriesId: string,
  days: number = 30
): Promise<{ value: number; percentage: number } | null> => {
  try {
    const history = await getSeriesNAVHistory(seriesId, days + 1);
    
    if (history.length < 2) {
      return null;
    }

    const latest = history[0].totalNAV;
    const previous = history[history.length - 1].totalNAV;
    const change = latest - previous;
    const percentage = (change / previous) * 100;

    return {
      value: change,
      percentage: parseFloat(percentage.toFixed(2)),
    };
  } catch (error) {
    console.error('Error calculating NAV change:', error);
    return null;
  }
};

/**
 * Get performance metrics for a series
 */
export const getSeriesPerformanceMetrics = async (seriesId: string) => {
  try {
    const history = await getSeriesNAVHistory(seriesId);
    
    if (history.length === 0) {
      return null;
    }

    const navValues = history.map(h => h.totalNAV);
    const latest = navValues[0];
    const initial = navValues[navValues.length - 1];
    const totalReturn = ((latest - initial) / initial) * 100;
    
    // Calculate max drawdown
    let maxDrawdown = 0;
    let peak = navValues[0];
    
    for (const nav of navValues) {
      if (nav > peak) {
        peak = nav;
      }
      const drawdown = ((peak - nav) / peak) * 100;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    return {
      currentNAV: latest,
      initialNAV: initial,
      totalReturn: parseFloat(totalReturn.toFixed(2)),
      maxDrawdown: parseFloat(maxDrawdown.toFixed(2)),
      dataPoints: history.length,
    };
  } catch (error) {
    console.error('Error calculating performance metrics:', error);
    return null;
  }
};
